

import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivityEntityName, MergeFieldData, MergeGroup, Entity } from '@jms/model';
import { SearchBoxModule } from '@jms/ui/ui.search.box';
import { DividerComponent } from '@workbuddy/ui/components/divider';
import { MergeFieldService } from '@jms/ui/ui.standalone.services/merge.field.service';
import { ObservableInput } from '@jms/utils/util.redux';
import { StateComponent } from '@jms/utils/util.state.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { Observable, switchMap, tap, map, catchError, of, combineLatest } from 'rxjs';

import * as Enumerable from 'linq';

export interface ExpandableMergeGroup extends MergeGroup {
    expanded: boolean;
}

export interface MergeFieldsPickerComponentVM {
    mergeGroups: MergeGroup[];
    filtered: ExpandableMergeGroup[];
    search: string;
    entities: Entity[];
    entity: ActivityEntityName;
}

const defaultState = {
    mergeGroups: [],
    filtered: [],
    search: null,
    entity: null,
    entities: [],
} as MergeFieldsPickerComponentVM;

@UntilDestroy()
@Component({
    selector: 'merge-fields-picker',
    templateUrl: './merge.fields.picker.component.html',
    styleUrls: ['./merge.fields.picker.component.less'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        SearchBoxModule,
        ReactiveFormsModule,
        ClipboardModule,
        DividerComponent
    ]
})
export class MergeFieldsPickerComponent extends StateComponent<MergeFieldsPickerComponentVM> {

    @ObservableInput() @Input('entity') public entity$: Observable<ActivityEntityName>;
    @ObservableInput() @Input('entityId') public entityId$: Observable<string>;

    constructor(
        private mergeFields: MergeFieldService,
        private messages: MessageService,
        private clipboard: Clipboard
    ) {
        super(defaultState);

        combineLatest([
            this.entity$,
            this.entityId$,
        ]).pipe(
            untilDestroyed(this),
            switchMap(([entity, entityId]) => this.mergeFields
                .getSources(entity, entityId)
                .pipe(
                    map(mergeGroups => {
                        return {
                            entity,
                            mergeGroups,
                        }
                    }),
                    catchError(error => {
                        console.log(error);
                        return of({
                            entity,
                            mergeGroups: []
                        })
                    })
                )
            ),
            tap(({ mergeGroups, entity }) => {

                const groups = this.getGroups(mergeGroups, entity, this.vm?.search);

                this.setStateDirect({
                    mergeGroups: groups,
                    filtered: groups as ExpandableMergeGroup[],
                    entities: this.getEntities(groups),
                    search: null,
                    entity: entity
                })

            })
        )
            .subscribe();

        this.createSelector(x => x.search)
            .pipe(
                untilDestroyed(this),
                tap(search => {
                    const groups = this.getGroups(this.vm.mergeGroups, this.vm.entity, search);

                    this.setState({
                        filtered: groups as any
                    })
                })
            )
            .subscribe();


    }

    public onSearch(search: string) {

        this.setStateDirect({
            search
        });
    }

    public copy(field: MergeFieldData, wordSyntax: boolean = false) {

        let fieldName = field.name;
        if (wordSyntax) {
            if (field.arrayFields?.length || field.isRepeat) {
                fieldName = `<<foreach [in ${field.name}]>>\n<</foreach>>`;
            }
            else {
                fieldName = `<<[${fieldName}]>>`
            }
        }

        const copied = this.clipboard.copy(fieldName);

        if (copied) {
            this.messages.add({
                severity: 'success',
                summary: `Copy Field`,
                detail: `${fieldName} was copied ${wordSyntax ? "with word syntax" : ''} to the clipboard`
            });
        }
    }

    public copyTitle(title: string) {
        const copied = this.clipboard.copy(title);

        if (copied) {
            this.messages.add({
                severity: 'success',
                summary: `Copy Title`,
                detail: `"${title}" was copied to the clipboard`
            });
        }
    }

    public toggleEntity(item: Entity) {
        item.active = !item.active;
        this.setStateDirect({
            filtered: this.getGroups(this.vm?.mergeGroups, this.vm.entity, this.vm.search) as any
        });
    }

    public getCount(group: ExpandableMergeGroup) {
        return Enumerable.from(group?.fields).where(x => x.hidden == false).count();
    }

    private escapeRegExp(value: string): string {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private filterFieldDeep(field: MergeFieldData, search: string): MergeFieldData | null {
        const searchLower = search?.toLowerCase();
        const matchesSelf = !search
            || `${field.displayName}`.toLowerCase().indexOf(searchLower) !== -1
            || `${field.name}`.toLowerCase().indexOf(searchLower) !== -1;

        let filteredNestedFields: MergeFieldData[] = null;
        if (field.fields?.length) {
            filteredNestedFields = field.fields
                .map(f => this.filterFieldDeep(f, search))
                .filter(f => f !== null);
        }

        // Array children are a separate branch on the server's shape, not nested fields: the merge
        // metadata puts every line-level field under `arrayFields`. Searching only `fields` left the
        // whole line block unreachable — `invoicingStatus`, `quantity`, `price` and the rest matched
        // nothing, and the array that holds them was culled for not matching the term itself.
        let filteredArrayFields: MergeFieldData[] = null;
        if (field.arrayFields?.length) {
            filteredArrayFields = field.arrayFields
                .map(f => this.filterFieldDeep(f, search))
                .filter(f => f !== null);
        }

        const hasMatchingChildren = filteredNestedFields?.length > 0 || filteredArrayFields?.length > 0;
        if (!matchesSelf && !hasMatchingChildren) {
            return null;
        }

        const highlight = search
            ? field.displayName.replace(new RegExp(this.escapeRegExp(search), 'gi'), (match) => `<mark>${match}</mark>`)
            : field.displayName;

        return {
            ...field,
            highlightName: highlight,
            // A field that matches by its own name keeps all of its children: searching "lines" asks
            // for the line block, not for the one child that happens to repeat the word.
            fields: matchesSelf ? field.fields : filteredNestedFields,
            arrayFields: matchesSelf ? field.arrayFields : filteredArrayFields
        };
    }

    private getGroups(mergeGroups: MergeGroup[], entity: ActivityEntityName, search: string) {
        if (!search?.length) {
            search = null;
        }

        const hasEntities = this.vm.entities?.length > 0;

        const activeEntities = Enumerable
            .from(this.vm.entities)
            .where(x => x.active)
            .select(x => x.name)
            .toArray();

        // When entity tags exist but the user has deselected all of them,
        // show no fields rather than falling back to showing everything.
        if (hasEntities && !activeEntities.length) {
            return [];
        }

        // Preserve original order
        const groups = Enumerable
            .from(mergeGroups)
            .where(g => !activeEntities?.length || activeEntities.includes(g.entity as any))
            .select(g => {
                const filteredFields = g.fields
                    .map(f => this.filterFieldDeep(f, search))
                    .filter(f => f !== null);

                return {
                    ...g,
                    fields: filteredFields
                };
            })
            .where(x => !!x?.fields?.length)
            .toArray();

        return groups;
    }

    private getEntities(groups: MergeGroup[]) {
        return Enumerable
            .from(groups)
            .select(x => {
                let name = `${x.entity}`;

                switch (x.entity) {
                    case ActivityEntityName.WorkOrder:
                        name = 'Stage';
                        break;

                    // Special case for timesheet because TimeSheet = 'Time Sheet' has 2 words
                    case ActivityEntityName.TimeSheet:
                        name = 'Timesheet';
                        break;

                    case ActivityEntityName.Allocation:
                        name = 'Appointment';
                        break;
                }

                return { name: x.entity, active: true, data: name }
            })
            .distinct(x => x.name)
            .toArray() as Entity[];
    }

}
